import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {Assignee} from './assignee'
import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {buildItemPickerSearchUrl} from '@github-ui/item-picker-base/api/urls'

/**
 * The types here align with the query made on the server.
 * If the shape of that response changes, this type should be
 * updated to reflect any modified fields
 */

export type GetSearchAssigneesRequest = {
  owner: string
  repo: string
  query?: string
  count?: number
  number?: number
  loginNames?: string
  capabilities?: string[]
}

type GetSearchAssigneesResponseData = {
  repository: {
    id: string
    assignableUsers?: {
      nodes: Assignee[]
      totalCount: number
    }
    issueOrPullRequest?: {
      suggestedActors?: {
        nodes: Assignee[]
      }
    }
    suggestedActors?: {
      nodes: Assignee[]
      totalCount: number
    }
  }
}

export type GetSearchAssigneesResponse = GraphQLResponse<GetSearchAssigneesResponseData>

export async function apiGetSearchAssignees({
  owner,
  repo,
  query,
  count,
  number,
  loginNames,
  capabilities,
}: GetSearchAssigneesRequest): Promise<GetSearchAssigneesResponse> {
  const params: Record<string, unknown> = {owner, repo}

  if (query) {
    params['query'] = query
  }

  if (count) {
    params['count'] = count
  }

  if (number !== undefined) {
    params['number'] = number
  }

  if (loginNames) {
    params['loginNames'] = loginNames
  }

  if (capabilities && capabilities.length > 0) {
    params['capabilities'] = capabilities.join(',')
  }

  const requestUrl = buildItemPickerSearchUrl('assignees', params)
  const request = await verifiedFetchJSON(requestUrl)
  return (await request.json()) as GetSearchAssigneesResponse
}

const itemPickerQueryKeyBase = 'item_picker'

export function buildAssigneePickerSearchQueryKey(searchParams: GetSearchAssigneesRequest) {
  return [itemPickerQueryKeyBase, 'assignees', searchParams]
}

export type AssigneePickerQueryData = {
  repositoryId: string
  assignees: Assignee[]
  totalCount?: number
}

export function transformSearchAssigneeResponseToQueryData(
  response: GetSearchAssigneesResponseData,
): AssigneePickerQueryData {
  // Handle different response structures based on the query type
  let assignees: Assignee[] = []
  let totalCount: number | undefined

  if (response.repository.suggestedActors) {
    assignees = response.repository.suggestedActors.nodes
    totalCount = response.repository.suggestedActors.totalCount
  } else if (response.repository.assignableUsers) {
    assignees = response.repository.assignableUsers.nodes
    totalCount = response.repository.assignableUsers.totalCount
  } else if (response.repository.issueOrPullRequest?.suggestedActors) {
    assignees = response.repository.issueOrPullRequest.suggestedActors.nodes
  }

  return {
    assignees,
    repositoryId: response.repository.id,
    totalCount,
  }
}
